// app/src/lib/revenue/pnl.js — monthly P&L rollup for the Reports cash-flow
// (Sankey) diagram: revenue by channel on the left, cost categories on the
// right. Each cost source is best-effort and independently guarded, so one
// module failing just omits that category rather than breaking the chart.

import { db } from '../db.js';
import { deliveryPlatformOf } from './depositRules.js';
import { currentClaims } from '../auth/cognitoClient.js';
import { settledMonthlyByChannel } from './settledSales.js';
import * as cogs from './cogs.js';
import * as rent from '../cashflow/rent.js';
import * as taxes from '../cashflow/taxes.js';
import * as insurance from '../cashflow/insurance.js';
import * as payroll from '../cashflow/payroll.js';
import { getKeywords as getExpenseKeywords, categorizeExpense } from '../cashflow/expenseKeywords.js';
import { isCardPayment, creditAccountIds, withoutCreditTxs } from '../cashflow/accounts.js';
import { loadExplicitContext, stampCardCategories } from '../cashflow/spendCategory.js';
import { monthlySummary as ownerDrawMonthly } from '../cashflow/ownerDraw.js';

function lastDayOfMonth(month) {
  const [y, m] = String(month).split('-').map(Number);
  return `${month}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
}

// 'YYYY-MM' of the current calendar month.
export function currentMonth(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// Shift a 'YYYY-MM' by n months.
export function shiftMonth(month, n) {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// Local 'YYYY-MM-DD' (device timezone), matching the rest of the app's date math.
function isoLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Cost categories over an arbitrary [from,to] date range. Each source is
 * best-effort and independently guarded, so one module failing just omits that
 * category. Shared by the monthly and rolling-30-day rollups.
 * @returns {Promise<{ costs: Array<{id:string,cents:number}>, totalCostsCents:number }>}
 */
async function computeCosts(userId, from, to) {
  // Range's card/bank transactions, shared by the keyword-classified costs.
  // This rollup is CHARGE-dated: card charges classify into their categories in
  // the month charged, so the card PAYMENT (the same dollars leaving checking)
  // must never classify as a cost here — even if a keyword happens to match its
  // descriptor — or the spend counts twice.
  let monthTxs = (await db.plaidTransactions
    .where('[userId+date]').between([userId, from], [userId, to], true, true)
    .toArray().catch(() => []))
    .filter((t) => !isCardPayment(t));
  // Wages + taxes are DEPOSITORY-only, exactly like their reports (the Payroll
  // and Taxes tabs), so each ribbon equals the report behind it. Their descriptor
  // matching (check ranges / "ADP TAX" keywords) could also false-positive on a
  // card charge; a card charge that matched would instead fall into "Other
  // costs", which keeps the pocket reconciliation intact. Rent/utilities and
  // insurance stay on monthTxs (card charges included) — their reports do too.
  const creditSet = await creditAccountIds(userId).catch(() => new Set());
  // Stamp card charges — and explicitly-categorized bank outflows (Today's
  // Recent-activity picker) — with the operator's EXPLICIT category, the same
  // pass the reports' loader runs, so a rerouted charge classifies here exactly
  // as it does on the report behind each ribbon. Must happen BEFORE depoTxs is
  // derived: the wages/taxes ribbons run on the depository subset and need the
  // stamps (e.g. a bare "ADP" IRS remittance ruled Taxes must leave wages).
  try {
    monthTxs = stampCardCategories(monthTxs, creditSet, await loadExplicitContext(userId));
  } catch { /* keyword-only classification */ }
  const depoTxs = withoutCreditTxs(monthTxs, creditSet);

  const costs = [];
  const push = (id, cents) => { if (cents > 0) costs.push({ id, cents: Math.round(cents) }); };

  // COGS — from scanned receipts/invoices (+ waste), via the COGS calc.
  try { const c = await cogs.calc({ from, to }); push('cogs', c.cogsCents || 0); } catch { /* omit */ }

  // Wages — payroll checks + ACH runs (depository, matches the Payroll report).
  try {
    const [ranges, taxKw] = await Promise.all([
      payroll.getCheckRanges().catch(() => []),
      taxes.getKeywords().catch(() => []),
    ]);
    const checks = payroll.summarizeChecks(depoTxs, ranges);
    const ach = payroll.summarizeAch(depoTxs, taxKw);
    push('wages', (checks.total || 0) + (ach.runsCents || 0));
  } catch { /* omit */ }

  // Rent & Utilities — rent keywords + utility-classified expenses — and its
  // sibling Maintenance ribbon (maintenance-keyword matches + charges the
  // operator explicitly categorized maintenance).
  try {
    const [rentKw, expKw] = await Promise.all([
      rent.getKeywords().catch(() => []),
      getExpenseKeywords().catch(() => ({})),
    ]);
    const rentCents = rent.summarize(monthTxs, rentKw).total || 0;
    let utilCents = 0, mroCents = 0;
    for (const tx of monthTxs) {
      if (tx.pending) continue; // same posted-only rule as the other categories
      // Explicitly-categorized maintenance rows belong here regardless of
      // keywords (refunds net out, same as the Supplies ribbon).
      if (tx._spendCat === 'maintenance') { mroCents += tx.amountCents || 0; continue; }
      // Other stamped charges are already routed by their explicit category:
      // rent.summarize above counts _spendCat==='rent_utilities' (as utility),
      // and every other stamped category belongs to its own ribbon — counting
      // any of them here would double the spend.
      if (tx._spendCat) continue;
      if ((tx.amountCents || 0) <= 0) continue;
      // Rent takes precedence, same as the report: an NNN payment naming
      // utilities/repairs is already inside rent.summarize's total.
      if (rent.isRent(tx, rentKw)) continue;
      const exp = categorizeExpense(tx.merchantName, tx.description, expKw);
      if (exp === 'utility') utilCents += tx.amountCents;
      else if (exp === 'maintenance') mroCents += tx.amountCents;
    }
    push('rent_utilities', rentCents + utilCents);
    push('maintenance', mroCents);
  } catch { /* omit */ }

  // Taxes (depository, matches the Taxes report).
  try {
    const taxKw = await taxes.getKeywords().catch(() => []);
    push('taxes', taxes.summarize(depoTxs, taxKw).totals.total || 0);
  } catch { /* omit */ }

  // Insurance.
  try {
    const insKw = await insurance.getKeywords().catch(() => []);
    push('insurance', insurance.summarize(monthTxs, insKw).totalCents || 0);
  } catch { /* omit */ }

  // Supplies — card charges categorized Supplies on the Spending tab (same
  // source as the Supplies report; the CSV order ledger was retired 2026-07-11).
  try {
    let sup = 0;
    for (const tx of monthTxs) {
      if (tx.pending) continue;
      if (tx._spendCat === 'supplies') sup += tx.amountCents || 0; // refunds net out
    }
    push('supplies', sup);
  } catch { /* omit */ }

  const totalCostsCents = costs.reduce((s, c) => s + c.cents, 0);
  return { costs, totalCostsCents };
}

function pnlResult(extra, revenueCents, inHouseCents, deliveryCents, costs, totalCostsCents) {
  const sources = [
    { id: 'instore', cents: Math.round(inHouseCents) },
    { id: 'delivery', cents: Math.round(deliveryCents) },
  ].filter((s) => s.cents > 0);
  return {
    ...extra,
    sources,
    revenueCents: Math.round(revenueCents),
    costs,
    totalCostsCents,
    netCents: Math.round(revenueCents) - totalCostsCents,
  };
}

/**
 * @returns {Promise<null | {
 *   month: string, mode:'month', from:string, to:string,
 *   sources: Array<{ id:'instore'|'delivery', cents:number }>,
 *   revenueCents: number,
 *   costs: Array<{ id:string, cents:number }>,
 *   totalCostsCents: number,
 *   netCents: number,
 * }>}
 */
export async function monthlyPnl(month) {
  const claims = await currentClaims();
  if (!claims) return null;
  const userId = claims.sub;
  const from = `${month}-01`, to = lastDayOfMonth(month);

  // ── Revenue split (settled, per channel) ────────────────────────────────
  let inHouseCents = 0, deliveryCents = 0;
  try {
    const s = await settledMonthlyByChannel(month);
    inHouseCents = s.inHouseCents || 0;
    deliveryCents = s.deliveryCents || 0;
  } catch { /* leave zero */ }

  let { costs, totalCostsCents } = await computeCosts(userId, from, to);

  // ── Reconcile the cost side against the pocket ─────────────────────────────
  // The keyword categories above can't catch everything (merchant fees, payroll
  // processing fees, software, misc), so revenue − categorized costs OVERSTATES
  // net profit. The month's true operating net is the Cash flow report's pocket
  // (ownerDraw.monthlySummary — the same engine Today's pocket now uses); the
  // gap becomes an explicit "Other costs" node so the diagram's Net profit
  // equals the pocket. Guarded on the month actually having bank deposits: with
  // no feed (manual sales only) the pocket is meaningless and the diagram stays
  // categorized-only. A negative gap (categorized costs already past the
  // pocket, e.g. receipt-COGS ahead of the supplier payments) adds nothing —
  // the Sankey's "Other funds" source balances a heavy cost side.
  try {
    const hasDeposit = await db.plaidTransactions
      .where('[userId+date]').between([userId, from], [userId, to], true, true)
      .filter((t) => !t.pending && (t.amountCents || 0) < 0)
      .first();
    if (hasDeposit) {
      const now = new Date();
      const [y, m] = month.split('-').map(Number);
      const monthsBack = (now.getFullYear() - y) * 12 + (now.getMonth() + 1 - m) + 1;
      if (monthsBack >= 1) {
        const od = await ownerDrawMonthly({ months: monthsBack });
        const row = (od.series || []).find((x) => x.ym === month);
        if (row && row.pocketCents != null) {
          const gap = Math.round((inHouseCents + deliveryCents) - totalCostsCents - row.pocketCents);
          if (gap > 0) { costs = [...costs, { id: 'other', cents: gap }]; totalCostsCents += gap; }
        }
      }
    }
  } catch { /* leave categorized-only */ }

  return pnlResult({ month, mode: 'month', from, to }, inHouseCents + deliveryCents, inHouseCents, deliveryCents, costs, totalCostsCents);
}

/**
 * Rolling last-30-days P&L, for the in-progress current month where a partial
 * calendar month would understate. Revenue is RAW deposit-dated (by channel
 * TYPE) over the window — the same basis the Today tab's 30-day view uses —
 * because settled month-attribution is a per-month rollup, not a per-day one.
 * No "Other costs" reconciliation here: the pocket engine is month-bucketed,
 * so a rolling window has no pocket to reconcile against.
 * @returns {Promise<null | { month:string, mode:'last30', from:string, to:string, sources, revenueCents, costs, totalCostsCents, netCents }>}
 */
export async function rolling30Pnl(now = new Date()) {
  const claims = await currentClaims();
  if (!claims) return null;
  const userId = claims.sub;
  const to = isoLocal(now);
  const fromD = new Date(now); fromD.setDate(fromD.getDate() - 29);
  const from = isoLocal(fromD);

  // Raw revenue by channel TYPE over the window (delivery vs in-house).
  let inHouseCents = 0, deliveryCents = 0;
  try {
    const channels = await db.channels.where('userId').equals(userId).toArray();
    const isDelivery = new Map(channels.map((c) => [c.channelId, !!deliveryPlatformOf(c)]));
    const entries = await db.salesEntries.where('[userId+date]').between([userId, from], [userId, to], true, true).toArray();
    for (const e of entries) {
      const n = e.netSalesCents || 0;
      if (isDelivery.get(e.channelId)) deliveryCents += n; else inHouseCents += n;
    }
  } catch { /* leave zero */ }

  const { costs, totalCostsCents } = await computeCosts(userId, from, to);
  return pnlResult({ month: currentMonth(now), mode: 'last30', from, to }, inHouseCents + deliveryCents, inHouseCents, deliveryCents, costs, totalCostsCents);
}
